import { mapKeys } from '@exodus/basic-utils';
import createIOC from '@exodus/dependency-injection';
import { createNoopLogger } from '@exodus/logger';
import { combineReducers } from 'redux';
import generateSelectorDependencies from './generate-selector-dependencies.js';
import generateReduxModuleDependency from './generate-redux-module-dependency.js';
import generateActionDependencies from './generate-action-dependencies.js';
import { createDefaultInitialState, createModule } from './defaults.js';
import eventToAction from './event-to-action.js';
const isReduxModuleDefinition = (dependency) => dependency.type === 'redux-module';
const isDependencyNodeDefinition = (dependency) => 'factory' in dependency && typeof dependency.factory === 'function';
const checkReducerConflicts = ({ baseReducers, reduxModuleDefinitions, }) => {
    const reduxModuleIds = new Set(reduxModuleDefinitions.map(({ id }) => id));
    const conflicting = Object.keys(baseReducers).filter((key) => reduxModuleIds.has(key));
    if (conflicting.length > 0) {
        throw new Error(`refusing to overwrite reducers: ${conflicting.join(', ')}`);
    }
};
const mergeReducers = ({ baseReducers, reduxModules, }) => {
    const reduxModuleReducers = Object.create(null);
    for (const [moduleId, { reducer }] of Object.entries(reduxModules)) {
        reduxModuleReducers[moduleId] = reducer;
    }
    return { ...baseReducers, ...reduxModuleReducers };
};
const mergeActions = ({ baseActions, reduxModuleActions, }) => {
    reduxModuleActions = mapKeys(reduxModuleActions, (_, key) => key.replace(/\.actions$/, ''));
    return { ...baseActions, ...reduxModuleActions };
};
const getByType = ({ ioc, type, }) => ioc.getByType(type);
const getReduxModuleActions = ({ ioc, }) => {
    const reduxActions = getByType({ ioc, type: 'redux-actions' });
    return reduxActions;
};
const getSelectors = ({ selectors, }) => {
    const unsuffixed = mapKeys(selectors, (_, key) => key.replace(/\.selectors$/, ''));
    return Object.freeze(unsuffixed);
};
const buildInitialState = ({ reduxModuleDefinitions, }) => {
    const merged = Object.create(null);
    for (const { id, initialState } of reduxModuleDefinitions) {
        merged[id] = initialState || createDefaultInitialState();
    }
    return merged;
};
const explodeReduxModules = ({ reduxModuleDefinitions, createLogger, }) => [
    ...reduxModuleDefinitions.flatMap((definition) => generateSelectorDependencies({ ...definition, createLogger })),
    ...reduxModuleDefinitions
        .filter((definition) => !!definition.actionsDefinition)
        .map((definition) => generateActionDependencies({
        id: definition.id,
        actionsDefinition: definition.actionsDefinition,
    })),
    ...reduxModuleDefinitions.map(generateReduxModuleDependency),
];
const createEventHandler = ({ store, reduxModules, }) => (event, payload) => Object.entries(reduxModules).forEach(([moduleId, { eventReducers = Object.create(null), eventToActionMappers = Object.create(null) },]) => {
    const fn = eventReducers[event];
    if (fn) {
        store.dispatch({
            type: eventToAction({ moduleId, event }),
            payload,
        });
        return;
    }
    const actionCreator = eventToActionMappers[event];
    if (actionCreator) {
        store.dispatch(actionCreator(payload));
    }
});
const setupIOC = ({ dependencies: dependencyDefinitions, logger, createLogger, }) => {
    const reduxModuleDefinitions = dependencyDefinitions.filter(isReduxModuleDefinition);
    const otherDependencies = dependencyDefinitions.filter(isDependencyNodeDefinition);
    const normalizedModules = reduxModuleDefinitions.map(createModule);
    const explodedDependencies = explodeReduxModules({
        reduxModuleDefinitions: normalizedModules,
        createLogger,
    });
    const ioc = createIOC({ logger, injectDependenciesAsPositionalArguments: true }).registerMultiple([...explodedDependencies, ...otherDependencies]);
    ioc.resolve();
    return {
        ioc,
        reduxModuleDefinitions: normalizedModules,
    };
};
export function setupActions({ actions: baseActions = Object.create(null), ioc, }) {
    return mergeActions({
        baseActions,
        reduxModuleActions: getReduxModuleActions({ ioc: ioc }),
    });
}
const setupRedux = (params, ..._rejectWidenedSelectorIds) => {
    void _rejectWidenedSelectorIds;
    if ('initialState' in params) {
        throw new Error("setupRedux's `initialState` parameter has been removed. Move any non-module state into a redux-module's own `initialState` field.");
    }
    const { dependencies, reducers: baseReducers = Object.create(null), createLogger = createNoopLogger, } = params;
    const logger = createLogger('exodus:redux-ioc');
    const { ioc, reduxModuleDefinitions } = setupIOC({
        dependencies,
        logger,
        createLogger,
    });
    checkReducerConflicts({ baseReducers, reduxModuleDefinitions });
    const reduxModules = getByType({ ioc, type: 'redux-module' });
    const reducers = mergeReducers({ baseReducers, reduxModules });
    const combinedReducer = combineReducers(reducers);
    const selectors = getSelectors({ selectors: getByType({ ioc, type: 'selectors' }) });
    const initialState = buildInitialState({ reduxModuleDefinitions });
    const createHandleEvent = (store) => createEventHandler({ store, reduxModules });
    return {
        initialState,
        reducers,
        combinedReducer,
        resolvedDependencies: ioc.getAll(),
        selectors,
        createHandleEvent,
        ioc,
    };
};
export default setupRedux;
